import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Sidebar } from '@/components/Sidebar';
import { FilePreviewOverlay, FileThumbnail, getFileKind, loadFilePreview } from '@/components/FilePreview';
import { Folder, FolderOpen, Upload, Trash2, Download, ArrowLeft, Plus, Eye } from 'lucide-react';
const fmtSize = (b) => b < 1024 ? `${b} Б` : b < 1048576 ? `${(b / 1024).toFixed(0)} КБ` : `${(b / 1048576).toFixed(1)} МБ`;

export default function FilesPage() {
  const [folders, setFolders] = useState([]);
  const [files, setFiles] = useState([]);
  const [sharedFolders, setSharedFolders] = useState([]);
  const [sharedFiles, setSharedFiles] = useState([]);
  const [currentFolder, setCurrentFolder] = useState('');
  const [breadcrumb, setBreadcrumb] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newFolderOpen, setNewFolderOpen] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [dragOver, setDragOver] = useState(false);
  const [preview, setPreview] = useState(null);
  const previewRequestRef = useRef(0);
  const previewUrlRef = useRef('');
  const [fileTab, setFileTab] = useState('personal'); // 'personal' | 'shared'
  const [userRole, setUserRole] = useState('doctor');
  const navigate = useNavigate();

  const fetchTree = useCallback(async () => {
    try {
      const { data } = await api.get('/files/tree');
      setFolders(data.folders || []);
      setFiles(data.files || []);
      setSharedFolders(data.shared_folders || []);
      setSharedFiles(data.shared_files || []);
    } catch (err) {
      if (err.response?.status === 401) navigate('/login');
      if (err.response?.status === 403) navigate('/pricing');
    }
    setLoading(false);
  }, [navigate]);

  useEffect(() => {
    if (!document.cookie.includes('pp_csrf')) { navigate('/login'); return; }
    fetchTree();
    api.get('/auth/me').then(({ data }) => setUserRole(data.role || 'doctor')).catch(() => {});
  }, [navigate, fetchTree]);

  const isShared = fileTab === 'shared';
  const hasSharedAccess = userRole === 'owner' || userRole === 'admin';
  const activeFolders = isShared ? sharedFolders : folders;
  const activeFiles = isShared ? sharedFiles : files;
  const currentFolders = activeFolders.filter(f => f.parent_id === currentFolder);
  const currentFiles = activeFiles.filter(f => f.folder_id === currentFolder);

  const navigateToFolder = (folderId, folderName) => {
    if (folderId === '') {
      setCurrentFolder('');
      setBreadcrumb([]);
    } else {
      setCurrentFolder(folderId);
      setBreadcrumb(prev => {
        const idx = prev.findIndex(b => b.id === folderId);
        if (idx >= 0) return prev.slice(0, idx + 1);
        return [...prev, { id: folderId, name: folderName }];
      });
    }
  };

  const goUp = () => {
    if (breadcrumb.length <= 1) { navigateToFolder('', 'Корень'); }
    else { const parent = breadcrumb[breadcrumb.length - 2]; navigateToFolder(parent.id, parent.name); }
  };

  const createFolder = async () => {
    if (!newFolderName.trim()) return;
    try {
      await api.post('/files/folders', { name: newFolderName.trim(), parent_id: currentFolder, scope: isShared ? 'shared' : '' });
      setNewFolderOpen(false); setNewFolderName('');
      toast.success('Папка создана');
      fetchTree();
    } catch (err) { toast.error(err.response?.data?.detail || 'Ошибка'); }
  };

  const deleteFolder = async (id) => {
    if (!confirm('Удалить папку и все файлы внутри?')) return;
    try { await api.delete(`/files/folders/${id}`); toast.success('Удалено'); fetchTree(); } catch { toast.error('Ошибка'); }
  };

  const uploadFiles = async (fileList) => {
    for (const file of fileList) {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('folder_id', currentFolder);
      fd.append('note', '');
      fd.append('scope', isShared ? 'shared' : '');
      try {
        await api.post('/files/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      } catch (err) { toast.error(`${file.name}: ${err.response?.data?.detail || 'Ошибка'}`); }
    }
    toast.success(`Загружено файлов: ${fileList.length}`);
    fetchTree();
  };

  const deleteFile = async (id) => {
    try { await api.delete(`/files/${id}`); toast.success('Удалён'); fetchTree(); } catch { toast.error('Ошибка'); }
  };

  const downloadFile = async (f) => {
    try {
      const resp = await api.get(`/files/${f.id}/download`, { responseType: 'blob' });
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement('a'); a.href = url; a.download = f.original_name; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error('Ошибка'); }
  };

  const closePreview = useCallback(() => {
    previewRequestRef.current += 1;
    if (previewUrlRef.current) URL.revokeObjectURL(previewUrlRef.current);
    previewUrlRef.current = '';
    setPreview(null);
  }, []);

  useEffect(() => () => {
    previewRequestRef.current += 1;
    if (previewUrlRef.current) URL.revokeObjectURL(previewUrlRef.current);
  }, []);

  const openPreview = async (f) => {
    const requestId = ++previewRequestRef.current;
    if (previewUrlRef.current) URL.revokeObjectURL(previewUrlRef.current);
    previewUrlRef.current = '';
    setPreview({ file: f, kind: getFileKind(f.original_name), loading: true });
    try {
      const result = await loadFilePreview(
        f,
        `/files/${f.id}/download`,
        `/files/${f.id}/preview`,
      );
      if (requestId !== previewRequestRef.current) {
        if (result.url) URL.revokeObjectURL(result.url);
        return;
      }
      previewUrlRef.current = result.url || '';
      setPreview(result);
    } catch (err) {
      if (requestId !== previewRequestRef.current) return;
      setPreview({
        file: f,
        kind: getFileKind(f.original_name),
        loading: false,
        error: err.response?.data?.detail || 'Проверьте соединение и попробуйте ещё раз.',
      });
    }
  };

  const handleDrop = (e) => { e.preventDefault(); setDragOver(false); if (e.dataTransfer.files.length) uploadFiles([...e.dataTransfer.files]); };

  return (
    <div className="flex min-h-screen" style={{ background: 'var(--app-bg)' }}>
      <Sidebar />
      <main className="flex-1 p-4 lg:p-8 pt-16 lg:pt-8 overflow-auto">
        <div className="max-w-full lg:max-w-5xl mx-auto">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6 animate-fade-in">
            <h1 className="font-heading text-2xl lg:text-3xl" style={{ color: 'var(--app-text)' }}>Файлы</h1>
            <div className="flex gap-2">
              <Button size="sm" onClick={() => setNewFolderOpen(true)} style={{ background: 'var(--app-card)', borderColor: 'var(--app-border)', color: 'var(--app-text-secondary)' }} variant="outline">
                <Plus size={14} className="mr-1" /> Папка
              </Button>
              <label className="cursor-pointer">
                <input type="file" className="hidden" multiple onChange={e => e.target.files?.length && uploadFiles([...e.target.files])} />
                <span className="inline-flex items-center gap-1.5 px-3 h-9 rounded-md text-xs cursor-pointer text-white" style={{ background: 'var(--app-accent)' }}>
                  <Upload size={13} /> Загрузить
                </span>
              </label>
            </div>
          </div>

          {/* Tab switcher: Personal / Shared (admin/owner only) */}
          {hasSharedAccess && (
            <div className="flex gap-1 mb-4 bg-stone-800/30 rounded-lg p-0.5 w-fit animate-fade-in">
              <button onClick={() => { setFileTab('personal'); setCurrentFolder(''); setBreadcrumb([]); }}
                className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${fileTab === 'personal' ? 'bg-amber-700 text-white' : 'text-stone-500'}`}>
                Мои файлы
              </button>
              <button onClick={() => { setFileTab('shared'); setCurrentFolder(''); setBreadcrumb([]); }}
                className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${fileTab === 'shared' ? 'bg-teal-700 text-white' : 'text-stone-500'}`}>
                Общее облако
              </button>
            </div>
          )}

          {/* Breadcrumb */}
          <div className="flex items-center gap-1 text-sm mb-4 flex-wrap" style={{ color: 'var(--app-text-muted)' }}>
            <button onClick={() => navigateToFolder('', 'Корень')} className="hover:underline" style={{ color: currentFolder ? 'var(--app-accent)' : 'var(--app-text)' }}>Файлы</button>
            {breadcrumb.map((b, i) => (
              <span key={b.id} className="flex items-center gap-1">
                <span>/</span>
                <button onClick={() => navigateToFolder(b.id, b.name)} className="hover:underline"
                  style={{ color: i === breadcrumb.length - 1 ? 'var(--app-text)' : 'var(--app-accent)' }}>{b.name}</button>
              </span>
            ))}
          </div>

          {/* Drop zone */}
          <div
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={handleDrop}
            className={`rounded-xl border-2 border-dashed transition-colors min-h-[300px] p-4 ${dragOver ? 'border-opacity-100' : 'border-opacity-30'}`}
            style={{ borderColor: dragOver ? 'var(--app-accent)' : 'var(--app-border)', background: dragOver ? 'var(--app-accent-muted)' : 'transparent' }}
          >
            {loading ? <p className="text-center py-12" style={{ color: 'var(--app-text-muted)' }}>Загрузка...</p> : (
              <>
                {currentFolder && (
                  <button onClick={goUp} className="flex items-center gap-2 p-3 rounded-lg mb-2 w-full text-left transition-colors"
                    style={{ color: 'var(--app-text-muted)' }}>
                    <ArrowLeft size={16} /> Назад
                  </button>
                )}

                {/* Folders */}
                {currentFolders.map(f => (
                  <div key={f.id} className="flex items-center gap-3 p-3 rounded-lg mb-1 transition-colors cursor-pointer group"
                    style={{ background: 'var(--app-card)' }}
                    onClick={() => navigateToFolder(f.id, f.name)}>
                    <Folder size={20} style={{ color: 'var(--app-accent)' }} />
                    <span className="flex-1 text-sm font-medium" style={{ color: 'var(--app-text)' }}>{f.name}</span>
                    <span className="text-xs" style={{ color: 'var(--app-text-faint)' }}>{files.filter(x => x.folder_id === f.id).length} файлов</span>
                    <button onClick={(e) => { e.stopPropagation(); deleteFolder(f.id); }}
                      className="opacity-0 group-hover:opacity-100 transition-opacity" style={{ color: 'var(--app-text-faint)' }}><Trash2 size={14} /></button>
                  </div>
                ))}

                {/* Files */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 mt-2">
                  {currentFiles.map(f => (
                    <div key={f.id} className="rounded-lg overflow-hidden border group" style={{ background: 'var(--app-card)', borderColor: 'var(--app-border)' }}>
                      <FileThumbnail file={f} downloadUrl={`/files/${f.id}/download`} onOpen={() => openPreview(f)} />
                      <div className="p-3">
                        <p className="text-sm truncate" style={{ color: 'var(--app-text)' }}>{f.original_name}</p>
                        <p className="text-xs mt-0.5" style={{ color: 'var(--app-text-faint)' }}>{fmtSize(f.size)}{f.note && ` · ${f.note}`}</p>
                        <div className="flex gap-1 mt-2">
                          <button onClick={() => openPreview(f)} className="p-1 rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-600" style={{ color: 'var(--app-text-muted)' }} title="Предпросмотр" aria-label={`Предпросмотр файла ${f.original_name}`}><Eye size={14} /></button>
                          <button onClick={() => downloadFile(f)} className="p-1 rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-600" style={{ color: 'var(--app-text-muted)' }} title="Скачать" aria-label={`Скачать файл ${f.original_name}`}><Download size={14} /></button>
                          <button onClick={() => deleteFile(f.id)} className="p-1 rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-600" style={{ color: 'var(--app-text-faint)' }} title="Удалить" aria-label={`Удалить файл ${f.original_name}`}><Trash2 size={14} /></button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {currentFolders.length === 0 && currentFiles.length === 0 && !currentFolder && (
                  <div className="text-center py-16">
                    <FolderOpen size={48} className="mx-auto mb-3" style={{ color: 'var(--app-text-faint)' }} />
                    <p className="text-sm" style={{ color: 'var(--app-text-muted)' }}>Перетащите файлы сюда или нажмите "Загрузить"</p>
                    <p className="text-xs mt-1" style={{ color: 'var(--app-text-faint)' }}>Папки клиентов создаются автоматически</p>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </main>

      {/* New folder dialog */}
      <Dialog open={newFolderOpen} onOpenChange={setNewFolderOpen}>
        <DialogContent className="bg-stone-900 border-stone-800 text-stone-200 max-w-sm">
          <DialogHeader><DialogTitle className="font-heading text-stone-200">Новая папка</DialogTitle></DialogHeader>
          <Input value={newFolderName} onChange={e => setNewFolderName(e.target.value)} placeholder="Название"
            onKeyDown={e => e.key === 'Enter' && createFolder()}
            className="bg-stone-950/50 border-stone-800 text-stone-200" />
          <DialogFooter>
            <Button variant="secondary" onClick={() => setNewFolderOpen(false)} className="bg-stone-800 text-stone-300">Отмена</Button>
            <Button onClick={createFolder} className="text-white" style={{ background: 'var(--app-accent)' }}>Создать</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <FilePreviewOverlay preview={preview} onClose={closePreview} onDownload={downloadFile} onRetry={openPreview} />
    </div>
  );
}
